import { parseUnits } from 'viem';
import { ActionRejectedException, CodeException, ErrorGettingTokenQuoteException, } from '../../../../errors';
import { selectUserOpsByAddress } from '../../../../store/userOpsStore';
import { estimateGasFeesWithReplacement } from '../estimateGasFees';
import { createPaymasterRequest } from './createPaymasterRequest';
import { payGasWithUSDC } from './payGasWithUSDC';
import { addUSDCEncodedApprovalToCallData } from './addUSDCEncodedApprovalToCallData';
import { quoteGasWithUSDC } from './quoteGasWithUSDC';
import { sponsorGas } from './sponsorGas';
export async function prepareSponsoredOrUSDCGasOperation(args) {
    const { userOp, rpcClient, fetchAccessTokenFn, rootKeyAddress, bundlerUrl, paymasterProxyUrl, paymasterProxyAuthSecret, bundlerClient, enableUsdc, hooks: _hooks, } = args;
    const { current, pending } = selectUserOpsByAddress(userOp.sender);
    const { functionHashForPaymasterProxy, spaceId } = current;
    let accessToken;
    try {
        accessToken = (await fetchAccessTokenFn?.()) ?? undefined;
    }
    catch (error) {
        throw new CodeException({
            message: 'Failed to get access token',
            code: 'USER_OPS_FAILED_ACCESS_TOKEN',
            data: error,
            category: 'userop',
        });
    }
    let maxFeePerGasOverride;
    let maxPriorityFeePerGasOverride;
    if (pending.hash && userOp.sender) {
        const result = await estimateGasFeesWithReplacement({
            sender: userOp.sender,
            client: rpcClient,
        });
        maxFeePerGasOverride = result.maxFeePerGas;
        maxPriorityFeePerGasOverride = result.maxPriorityFeePerGas;
    }
    const rpcR = createPaymasterRequest({
        userOp: userOp,
        functionHashForPaymasterProxy,
        rootKeyAddress,
        townId: spaceId,
        maxFeePerGasOverride,
        maxPriorityFeePerGasOverride,
    });
    if (!rpcR.callData) {
        throw new Error('rpcRequest.callData is required');
    }
    let gas;
    let shouldPayWithUSDC;
    if (typeof localStorage !== 'undefined' && localStorage.getItem('usdcgas') === 'true') {
        gas = undefined;
        shouldPayWithUSDC = true;
    }
    else {
        const result = await sponsorGas({
            userOp: userOp,
            rpcRequest: rpcR,
            bundlerUrl,
            accessToken,
            usePimlicoPaymaster: false,
            paymasterProxyUrl,
            paymasterProxyAuthSecret,
            enableUsdc,
        });
        gas = result.gas;
        shouldPayWithUSDC = result.shouldPayWithUSDC;
    }
    if (gas) {
        return {
            gas,
            callData: rpcR.callData,
        };
    }
    else if (shouldPayWithUSDC) {
        // TODO: allow the client to pass a buffer to quoteGasWithUSDC
        // we're going to add the USDC approval next, so the extra buffer is needed, once the USDC approval is added, the gas will be marginally higher
        const tokenQuote = await quoteGasWithUSDC({
            rpcRequest: rpcR,
            smartAccountClient: bundlerClient,
            accessToken,
            paymasterProxyAuthSecret,
            paymasterProxyUrl,
            bundlerUrl,
            enableUsdc,
        });
        if (!tokenQuote) {
            throw new ErrorGettingTokenQuoteException();
        }
        const hookResult = await _hooks?.postTokenQuote?.({
            userOp: userOp,
            tokenQuote,
        });
        if (hookResult?.cancel) {
            throw new ActionRejectedException();
        }
        const estimatedUSDCTokens = parseUnits(tokenQuote.estimate, tokenQuote.decimals);
        // add the USDC approval to the call data
        const rpcRequestWithApproval = {
            ...rpcR,
            callData: await addUSDCEncodedApprovalToCallData({
                callData: rpcR.callData,
                smartAccountClient: bundlerClient,
                allowance: estimatedUSDCTokens * 2n, // double approve the amount of gas just to be safe
            }),
        };
        const result = await payGasWithUSDC({
            rpcRequestWithUSDCApproval: rpcRequestWithApproval,
            accessToken,
            paymasterProxyAuthSecret,
            paymasterProxyUrl,
            bundlerUrl,
            enableUsdc,
        });
        if (result) {
            return {
                gas: result.gas,
                callData: result.callData,
            };
        }
        else {
            // the op will not be sponsored, the user cannot pay with USDC, but they can still pay gas with native ETH...what to do here for our case?
            return;
        }
    }
}
